import {
  createContext,
  useContext,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

import type { GetUserReportingsFilters } from "@tudigo-monorepo/core-tudigo-api";
import { useTranslation } from "@tudigo-monorepo/core-tudigo-translations";
import { useUserBusiness } from "@tudigo-monorepo/tudigo-session";
import {
  createFilterHelper,
  type FilterConfiguration,
} from "@tudigo-monorepo/web-tudigo-collection";

const ParticipationsReportingsFiltersContext = createContext<{
  configuration: Array<FilterConfiguration<GetUserReportingsFilters>>;
  filters: GetUserReportingsFilters;
  setFilters: Dispatch<SetStateAction<GetUserReportingsFilters>>;
} | null>(null);

export function useParticipationsReportingsFilters() {
  const context = useContext(ParticipationsReportingsFiltersContext);
  if (context == null) {
    throw new Error(
      "useParticipationsReportingsFilters must be used within a ParticipationsReportingsFiltersProvider",
    );
  }

  return context;
}

type ParticipationsReportingsFiltersProviderProps = {
  children: ReactNode;
  defaultFilters?: GetUserReportingsFilters;
};

export function ParticipationsReportingsFiltersProvider({
  children,
  defaultFilters = {},
}: ParticipationsReportingsFiltersProviderProps) {
  const [filters, setFilters] =
    useState<GetUserReportingsFilters>(defaultFilters);

  const { t } = useTranslation();
  const { userIdOrMe } = useUserBusiness();
  const filterHelper = createFilterHelper<GetUserReportingsFilters>();

  const configuration: FilterConfiguration<GetUserReportingsFilters>[] = [
    filterHelper.organizationSelect(
      "organizationId",
      t("participations.web_app.reporting_list_page.filters.projects.label"),
      { type: "company", userId: userIdOrMe },
    ),
    filterHelper.reportingPeriodSelect(
      "periodStartDate",
      t("participations.web_app.reporting_list_page.filters.period.label"),
      undefined,
      true,
    ),
  ];

  const contextValue = useMemo(
    () => ({ configuration, filters, setFilters }),
    [configuration, filters],
  );

  return (
    <ParticipationsReportingsFiltersContext.Provider value={contextValue}>
      {children}
    </ParticipationsReportingsFiltersContext.Provider>
  );
}
